import { getCustomProps } from '../forms'
import { getData } from '../get-table'
import { HttpService } from '../http.service'

const http = new HttpService()

export interface RecurringIncident {
  id?: string
  payrollId: string
  employees: any[]
  processes: any[]
  conceptId: string
  currencyId: string
  startDate: string
  endDate?: string
  quantity?: string
  amount?: string
  percentage?: string
  onePeriod: boolean
  twoPeriod: boolean
  threePeriod: boolean
  fourPeriod: boolean
  lastPeriod: boolean
  customFields: any[]
}

export interface DeleteRecurringIncidentRequests {
  id?: string
  user?: string
  observation?: string
  incidents?: any[]
  status?: string
  errors?: any[]
}

export async function getDeleteRecurringIncidentRequests(
  offset: number,
  perPage: number,
  dataOrder: string,
  search: string = ''
): Promise<any> {

  const where = ['payroll_id = :payrollId']
  if (search) {
    where.push(
      `CONCAT(delete_recurring_incident_requests.user,observation,process_status.name,errors) ILIKE '%${search}%'`
    )
  }

  let modifier = []
  dataOrder !== ''
    ? (modifier = [`order by ${dataOrder[1]} ${dataOrder[0]}`])
    : (modifier = ['order by delete_recurring_incident_requests.updated_at desc'])
    
  const response: any = await getData({
    tables: ['pg_delete_recurring_incident_requests'],
    fields: [
      'delete_recurring_incident_requests.id',
      'delete_recurring_incident_requests.user as name_user',
      'delete_recurring_incident_requests.observation',
      'delete_recurring_incident_requests.recurring_incidents',
      'process_status.name as status_name',
      'process_status.code as status_code',
      'delete_recurring_incident_requests.errors',
      'delete_recurring_incident_requests.updated_at',
      'delete_recurring_incident_requests.successful_rows',
      'delete_recurring_incident_requests.summary'
    ],
    join: [
      'INNER JOIN process_status ON delete_recurring_incident_requests.status_id = process_status.id'
    ],
    where,
    modifier,
    options: {
      outputFormat: 'json',
      pagination: { offset, limit: perPage }
    }
  })
  return response[0]
}

export async function getRecurringIncidents(
  offset: number,
  perPage: number,
  dataOrder: string,
  search: string = '',
  employeeId?: string,
  conceptId?: string,
  startDate?: string,
  endDate?: string,
  processId?: string
): Promise<any> {
  // where's
  const where = ['recurring_incidents.payroll_id = :payrollId']
  if (search) {
    where.push(
      `CONCAT(employees.code ,employees.name, processes.name, recurring_incidents.amount,recurring_incidents.end_date,recurring_incidents.percentage,recurring_incidents.quantity,recurring_incidents.start_date,concepts.name) ILIKE '%${search}%'`
    )
  }
  if (employeeId && employeeId !== '') {
    where.push(`employees.id = '${employeeId}'`)
  }
  if (conceptId && conceptId !== '') {
    where.push(`concepts.id = '${conceptId}'`)
  }
  if (startDate) {
    where.push(`recurring_incidents.start_date >= '${startDate}'`)
  }
  if (endDate) {
    where.push(`recurring_incidents.end_date <= '${endDate}'`)
  }
  if (processId && processId !== '') {
    where.push(`processes.id = '${processId}'`)
  }
  // order by
  const modifier = [
    'GROUP BY recurring_incidents.id, concepts.name, currencies.symbol'
  ]
  dataOrder !== ''
    ? modifier.push(`order by ${dataOrder[1]} ${dataOrder[0]}`)
    : modifier.push('order by recurring_incidents.created_at desc')
  // result
  const response: any = await getData({
    tables: ['pg_recurring_incidents'],
    fields: [
      'recurring_incidents.id as id',
      'recurring_incidents.id as ri_id',
      "string_agg(distinct employees.name,',') as employees_name",
      "string_agg(distinct processes.name,',') as processes_name",
      'concepts.name as concept_name',
      'recurring_incidents.start_date as ri_start_date',
      'recurring_incidents.end_date as ri_end_date',
      'recurring_incidents.quantity as ri_quantity',
      'recurring_incidents.amount as amount',
      'recurring_incidents.percentage as percentage',
      'recurring_incidents.updated_at as ri_updated_at',
      'recurring_incidents.payroll_id as ri_payroll_id',
      'currencies.symbol as currency_symbol'
    ],
    join: [
      'JOIN concepts ON recurring_incidents.concept_id = concepts.id',
      'LEFT JOIN currencies ON recurring_incidents.currency_id = currencies.id',
      'JOIN LATERAL jsonb_array_elements_text(recurring_incidents.employees) r(employee) ON TRUE',
      'JOIN employees ON employees.id = r.employee::uuid',
      'JOIN LATERAL jsonb_array_elements_text(recurring_incidents.processes) y(process) ON TRUE',
      'JOIN processes ON processes.id = y.process::uuid'
    ],
    where,
    modifier,
    options: {
      outputFormat: 'json',
      pagination: { offset, limit: perPage }
    }
  })
  return response
}

export async function getRecurringIncident(
  id: string
): Promise<RecurringIncident> {
  const data = await getData<any>({
    tables: ['pg_recurring_incidents'],
    fields: [
      'id',
      'amount',
      'concept_id as conceptId',
      'currency_id as currencyId',
      'employees',
      "TO_CHAR(end_date, 'yyyy-mm-dd') as endDate",
      'four_period as fourPeriod',
      'id',
      'last_period as lastPeriod',
      'one_period as onePeriod',
      'payroll_id as payrollId',
      'percentage',
      'processes',
      'quantity',
      "TO_CHAR(start_date, 'yyyy-mm-dd') as startDate",
      'three_period as threePeriod',
      'two_period as twoPeriod',
      'custom_fields as custom'
    ],
    where: [`id = '${id}'`],
    options: {
      outputFormat: 'json',
      pagination: { offset: 0, limit: 10 }
    }
  })
  return data[0].results[0]
}

export async function createRecurringIncident(
  data: RecurringIncident
): Promise<boolean> {
  const payload: RecurringIncident = {
    payrollId: data.payrollId,
    employees: data.employees,
    processes: data.processes,
    conceptId: data.conceptId,
    startDate: data.startDate,
    currencyId: data.currencyId,
    onePeriod: data.onePeriod,
    twoPeriod: data.twoPeriod,
    threePeriod: data.threePeriod,
    fourPeriod: data.fourPeriod,
    lastPeriod: data.lastPeriod,
    customFields: {
      ...getCustomProps(data)
    }
  }
  if (data.endDate !== '') payload.endDate = data.endDate
  if (data.amount !== '') payload.amount = data.amount
  if (data.percentage !== '') payload.percentage = data.percentage
  if (data.quantity !== '') payload.quantity = data.quantity

  const response: any = await http.post(
    'calculation/recurring_incidents',
    payload
  )

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function updateRecurringIncident(
  data: RecurringIncident,
  id: string
): Promise<boolean> {
  const payload: RecurringIncident = {
    payrollId: data.payrollId,
    employees: data.employees,
    processes: data.processes,
    conceptId: data.conceptId,
    startDate: data.startDate,
    currencyId: data.currencyId,
    onePeriod: data.onePeriod,
    twoPeriod: data.twoPeriod,
    threePeriod: data.threePeriod,
    fourPeriod: data.fourPeriod,
    lastPeriod: data.lastPeriod,
    customFields: {
      ...getCustomProps(data)
    }
  }
  if (data.endDate !== '') payload.endDate = data.endDate
  if (data.amount !== '') payload.amount = data.amount
  if (data.percentage !== '') payload.percentage = data.percentage
  if (data.quantity !== '') payload.quantity = data.quantity
  const response: any = await http.put(
    'calculation/recurring_incidents/' + id,
    payload
  )

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function deleteRecurringIncident(id: string): Promise<boolean> {
  const response: any = await http.delete(
    'calculation/recurring_incidents/' + id
  )

  if (response?.data?.data?.error) {
    return false
  }
  return true
}

export async function bulkDeleteRecurringIncidents(
  ids: string[],
  user: string,
  observation: string
): Promise<boolean> {
  const response: any = await http.post(
    'calculation/recurring_incidents/bulk-delete',
    { ids, user, observation }
  )

  if (response?.data?.data?.error) {
    return false
  }
  return true
}
